<template>
  <div>
    <div v-if="status === null" class="text-center q-pa-md">
      <q-icon name="fa fa-spinner fa-spin" size="3rem" />
    </div>
    <div v-else class="text-body1 text-weight-bold q-mb-md">
      <span v-if="isRunning" class="text-positive">
        Importing Posts
        <q-icon name="fa fa-spinner fa-spin" />
      </span>
      <q-btn-dropdown
        v-else label="Last Post Import"
        color="grey-2"
        text-color="dark"
        class="text-body1 text-weight-bold"
        rounded
        unelevated
        no-caps
      >
        <view-imports-btn
          :account-id="postImport.accountId"
          :platform="platform"
          label="View All"
          class="full-width"
          align="center"
          no-caps
        />
      </q-btn-dropdown>
      <job-run-time-info
        v-if="jobRun"
        :job-run="jobRun"
        class="text-grey-7 text-body1 on-right"
      />
    </div>
    <div v-if="isRunning" class="text-body1">
      <app-user v-slot="{ isServiceProvider, isCustomer, isClient }">
        <div v-if="isServiceProvider || isCustomer" class="q-mb-sm">
          So far, {{ postImport.numPosts }} posts have been imported.
        </div>
        <div v-if="isCustomer">
          You will be notified when all of the posts have been imported.
        </div>
        <div v-if="isClient">
          No further action is needed for {{ platform }}.
        </div>
      </app-user>
    </div>
    <post-import-insights
      v-else-if="succeeded && jobRun"
      :post-import="postImport"
      :job-run="jobRun"
    />
    <message v-else-if="failed" type="negative">
      A problem occurred during the import.
    </message>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { JobRun, JobStatus } from '@/type';
import { PostImport } from '@/social/types';
import AppUser from '@/auth/AppUser.vue';
import { ping } from '@/monitor/job/job-status-service';
import JobRunTimeInfo from '@/monitor/job/JobRunTimeInfo.vue';
import Message from '@/components/message/Message.vue';
import PostImportInsights from './PostImportInsights.vue';
import postImportRepository from '../post-import-repository';
import ViewImportsBtn from '../ViewImportsBtn.vue';

interface Data {
  cancelPing: { (): void }|null,
  jobRun: JobRun|null|undefined
}

export default defineComponent({
  emits: ['running', 'successful', 'failed'],
  components: {
    ViewImportsBtn,
    Message,
    AppUser,
    PostImportInsights,
    JobRunTimeInfo,
  },
  props: {
    postImport: {
      type: Object as () => PostImport,
      required: true,
    },
    platform: {
      type: String,
      required: true,
    },
  },
  data(): Data {
    return {
      cancelPing: null,
      jobRun: undefined,
    };
  },
  computed: {
    status(): JobStatus|null {
      if (this.jobRun === null) return 'RUNNING';
      if (this.jobRun) return this.jobRun.status;
      return null;
    },
    isRunning(): boolean {
      return (this.status === 'RUNNING');
    },
    succeeded(): boolean {
      return (this.status === 'SUCCESS');
    },
    failed(): boolean {
      return (this.status === 'FAILURE');
    },
  },
  watch: {
    postImport(newImport: PostImport, oldImport: PostImport): void {
      if (newImport.id !== oldImport.id) {
        this.ping();
      }
    },
    async status(status: JobStatus|null) {
      if (status === null) return;
      const eventNames = {
        RUNNING: 'running',
        SUCCESS: 'successful',
        FAILURE: 'failed',
      };
      const eventName = eventNames[status];
      this.$emit(eventName, await postImportRepository.find(this.postImport.id));
    },
  },
  methods: {
    cleanup(): void {
      if (this.cancelPing) {
        this.cancelPing();
      }
    },
    ping(): void {
      this.cleanup();
      this.jobRun = undefined;
      this.cancelPing = ping(this.postImport.jobId, (jobRun) => {
        this.jobRun = jobRun;
        if (jobRun && jobRun.status === 'SUCCESS') {
          this.cleanup();
        }
      });
    },
  },
  created(): void {
    this.ping();
  },
  beforeUnmount(): void {
    this.cleanup();
  },
});
</script>
